import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Loader2, Shield, User, Eye } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ username: '', password: '', role: 'admin' });
  const [deleteTarget, setDeleteTarget] = useState(null);

  const roles = [
    { value: 'admin', label: 'Administrator', description: 'Vollzugriff auf alle Funktionen' },
    { value: 'manager', label: 'Manager', description: 'Verwaltung von Teilnehmern und Textbausteinen' },
    { value: 'viewer', label: 'Betrachter', description: 'Nur Lesezugriff' }
  ];

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Benutzer');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingUser(null);
    setFormData({ username: '', password: '', role: 'admin' });
    setDialogOpen(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({ username: user.username, password: '', role: user.role });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('admin_token');

      if (editingUser) {
        const updateData = { role: formData.role };
        if (formData.password) {
          updateData.password = formData.password;
        }
        
        await axios.put(
          `${API}/admin/users/${editingUser.id}`,
          updateData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Benutzer aktualisiert');
      } else {
        await axios.post(
          `${API}/admin/users`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Benutzer erstellt');
      }

      setDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/users/${deleteTarget}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Benutzer gelöscht');
      setDeleteTarget(null);
      fetchUsers();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-blue-600 text-white';
      case 'manager': return 'bg-cyan-600 text-white';
      case 'viewer': return 'bg-green-600 text-white';
      default: return 'bg-gray-600 text-white';
    }
  };

  const getRoleLabel = (role) => {
    const r = roles.find(r => r.value === role);
    return r ? r.label : role;
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="py-3 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Benutzer ({users.length})</CardTitle>
          </div>
          <Button
            onClick={handleCreate}
            size="sm"
            data-testid="create-user-button"
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="h-4 w-4 mr-1" />
            Neuer Benutzer
          </Button>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Shield className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Keine Benutzer vorhanden</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Benutzername</TableHead>
                  <TableHead>Rolle</TableHead>
                  <TableHead>Erstellt am</TableHead>
                  <TableHead>Letzter Login</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">{user.username}</TableCell>
                    <TableCell>
                      <Badge className={getRoleBadgeColor(user.role)}>
                        {getRoleLabel(user.role)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString('de-DE') : '-'}
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {user.last_login ? new Date(user.last_login).toLocaleDateString('de-DE') : 'Noch nie'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(user)}
                          data-testid={`edit-user-${user.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <AlertDialog open={deleteTarget === user.id} onOpenChange={(open) => !open && setDeleteTarget(null)}>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeleteTarget(user.id)}
                            data-testid={`delete-user-${user.id}`}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Benutzer löschen?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Möchtest du den Benutzer &quot;{user.username}&quot; wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={handleDelete}
                                className="bg-red-600 hover:bg-red-700 text-white"
                              >
                                Löschen
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog - Modernisiert mit 2-Spalten-Layout */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl">{editingUser ? 'Benutzer bearbeiten' : 'Neuer Benutzer'}</DialogTitle>
            <DialogDescription className="text-base">
              {editingUser ? 'Bearbeite die Benutzerdetails.' : 'Erstelle einen neuen Administrator.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 2-Spalten Layout für Username und Password */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <User className="h-4 w-4" />
                  <span>Benutzername</span>
                </div>
                <Input
                  id="username"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  disabled={!!editingUser}
                  data-testid="user-username-input"
                  placeholder="z.B. admin"
                  className="text-base h-12"
                />
                {editingUser && (
                  <p className="text-xs text-gray-500">
                    ⚠️ Benutzername kann nicht geändert werden
                  </p>
                )}
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <Shield className="h-4 w-4" />
                  <span>Passwort</span>
                  {editingUser && <span className="text-xs font-normal text-gray-500">(optional)</span>}
                </div>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                  data-testid="user-password-input"
                  placeholder={editingUser ? 'Neues Passwort eingeben' : 'Sicheres Passwort'}
                  className="text-base h-12"
                />
                {editingUser && (
                  <p className="text-xs text-gray-500">
                    Leer lassen, um das aktuelle Passwort beizubehalten
                  </p>
                )}
              </div>
            </div>

            {/* Rolle - Volle Breite mit visueller Auswahl */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Shield className="h-4 w-4" />
                <span>Benutzerrolle</span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {roles.map(role => (
                  <button
                    key={role.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, role: role.value })}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      formData.role === role.value
                        ? 'border-blue-500 bg-blue-50 shadow-md'
                        : 'border-gray-200 bg-white hover:border-blue-300'
                    }`}
                  >
                    <p className={`font-semibold mb-1 ${
                      formData.role === role.value ? 'text-blue-900' : 'text-gray-900'
                    }`}>
                      {role.label}
                    </p>
                    <p className="text-xs text-gray-600">{role.description}</p>
                    {formData.role === role.value && (
                      <div className="mt-2 flex items-center gap-1 text-blue-600 text-xs font-medium">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Ausgewählt
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="text-base">
                Abbrechen
              </Button>
              <Button 
                type="submit" 
                className="bg-blue-600 hover:bg-blue-700 text-white text-base"
                data-testid="user-save-button"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Speichern
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersTab;